import { Component, Inject, OnDestroy, OnInit } from '@angular/core'
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms'
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete'
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog'
import { Observable, Subject, startWith, map } from 'rxjs'
import { Assignment, KeyBitArray } from '../../models/dev.test.models'
import { LoggingService } from '../../services/logging.service'

@Component({
  selector: 'app-reserve-dialog',
  templateUrl: './reserve-dialog.component.html',
  styleUrls: ['./reserve-dialog.component.scss'],
  standalone: false,
})
export class ReserveDialogComponent implements OnInit, OnDestroy {
  destroy$ = new Subject()
  reserveForm: FormGroup

  selectedKbaArray
  reservationTypes
  selectedReservationType
  reservedMasterKeyBittings
  selectedMasterKey
  skipErrors = false
  filteredReservationTypes!: Observable<any[]>

  focus = {
    reservationType: false,
    reservationNotes: false,
  }

  public errorMessages = {
    reservationType: [{ type: 'required', message: 'Reservation Type is required.' }],
    reservationNotes: [{ type: 'required', message: 'Notes are required.' }],
  }

  constructor(
    public form: FormBuilder,
    private dialogRef: MatDialogRef<ReserveDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any,
    private loggingService: LoggingService
  ) {
    this.reserveForm = this.form.group({
      reservationType: new FormControl('', [Validators.required]),
      reservationNotes: new FormControl('', [Validators.required, Validators.maxLength(2000)]),
    })
  }

  /* istanbul ignore next */
  ngOnDestroy(): void {
    this.destroy$.next(true)
    this.destroy$.complete()
  }

  /* istanbul ignore next */
  ngOnInit() {
    this.loggingService.log('Data:', this.data)
    this.reservationTypes = this.data.reservationTypes

    this.filteredReservationTypes = this.reserveForm.controls['reservationType'].valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : value?.description || value?.name || '')),
      map((name) => (name ? this._filterReservationTypes(name) : this.reservationTypes.slice()))
    )

    this.selectedKbaArray = this.data.selectedKbaArray
    this.selectedMasterKey = this.data.selectedMasterKey
  }

  /* istanbul ignore next */
  private _filterReservationTypes(value: string): any[] {
    const filterValue = value.toLowerCase()
    return this.reservationTypes.filter(
      (option: any) =>
        option.description?.toLowerCase().includes(filterValue) || option.name?.toLowerCase().includes(filterValue)
    )
  }

  /* istanbul ignore next */
  selectReservationType($event: MatAutocompleteSelectedEvent) {
    this.selectedReservationType = $event.option.value
  }

  /* istanbul ignore next */
  displayName(subject) {
    if (subject.name) {
      return (subject && subject.name) || subject
    } else if (subject.description) {
      return (subject && subject.description) || subject
    }
  }

  /* istanbul ignore next */
  reserve() {
    if (this.reserveForm.valid) {
      this.reserveKbas()
      const data = {
        selectedReservationType: this.selectedReservationType,
        reservationNotes: this.reserveForm.controls.reservationNotes.value,
        reservedKbas: this.selectedKbaArray,
      }

      this.dialogRef.close(data)
    }
  }

  /* istanbul ignore next */
  reserveKbas() {
    this.selectedKbaArray.forEach((kba: KeyBitArray) => {
      if (!kba?.assignment?.masterKey?.id) {
        kba.assignment = new Assignment()
        kba.assignment.reserved = true
        kba.assignment.reservationType = this.selectedReservationType
        kba.assignment.reservationNote = this.reserveForm.controls.reservationNotes.value
        kba.selected = false
      }
    })
    this.selectedKbaArray = []
  }

  /* istanbul ignore next */
  focusOn(focus) {
    this.focus[focus] = !this.focus[focus]
  }

  /* istanbul ignore next */
  fieldHasErrorMessages(form, validation, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.hasError(validation.type) && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  fieldHasError(form, fieldName) {
    const field = form.get(fieldName)

    return Boolean(field.invalid && (field.dirty || field.touched))
  }

  /* istanbul ignore next */
  close() {
    this.dialogRef.close()
  }
}
